<template>
  <div>
    <StepFormLayout
      class="job-editor-layout new-page-layout"
      :class="[
        showStandaloneStep && 'show-standalone-step',
        isQuestionStepActive && 'question-step-active'
      ]"
      :activeMenuIndex="currentStepIndex"
      :activeStandaloneMenuIndex="currentStandaloneStepIndex"
      :stepMenuList="activeStepConfigList"
      :standaloneStepMenuList="activeStandaloneStepConfigList"
      :isJobFitStepBtn="isJobFitStepBtn"
      :saveInLastStep="!isEditMode"
      :isPreview="isPreview"
      :hiddenStepBtn="hiddenStepBtn"
      :hiddenSaveBtn="hiddenSaveBtn"
      @step-change="handleStepChange"
      @standalone-step-change="handleActiveStandaloneMenuChange"
      @prev="handlePrevBtnClick"
      @next="handleNextBtnClick"
      @save="handleSaveAndBackBtnClick"
      @ai-comparison-save="handleAiComparisonSaveBtnClick"
      @job-fit-save="handleJobFitSaveBtnClick"
      @cancel="handleCancelBtnClick"
      @preview="handlePreviewBtnClick"
    >
      <component
        v-for="(item) in activeStepConfigList"
        v-bind="item.props"
        class="body-content"
        :key="item.stepType"
        :ref="`stepComponent_${item.stepType}`"
        :is="item.component"
        :show="showStep && item === stepConfig"
        :job-info="jobInfo"
        :is-edit-mode="isEditMode"
        :is-template="isTemplate"
        :companyDataParams="companyDataParams"
        :companyIsELC="companyIsELC"
      />
      <component
        v-for="item in activeStandaloneStepConfigList"
        v-bind="item.props"
        class="body-content"
        :key="item.stepType"
        :ref="`stepComponent_${item.stepType}`"
        :is="item.component"
        :show="showStandaloneStep && item === standaloneStepConfig"
        :job-info="jobInfo"
        :is-edit-mode="isEditMode"
        :is-template="isTemplate"
        :companyDataParams="companyDataParams"
        :showAiMatchTestCandidate="showAiMatchTestCandidate"
      />
    </StepFormLayout>
    <DialogWrapper
      :show.sync="showSaveJobTipDialog.show"
      :title="$t(`${i18nPrefix}.title.addJob`)"
      @save="handleSaveAndBackBtnClick(true)"
    >
      <span class="content-text">{{ $t(`${i18nPrefix}.message.pleaseConfirmToPostANewJob`) }}</span>
    </DialogWrapper>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import { addI18nProperty, getI18nPropertyValue } from '@/i18n'
import StepFormLayout from '@/components/layout/stepLayout/formLayout.vue'
import BaseInfo from './baseInfo.vue'
import AdditionalInfo from './additionalInfo.vue'
import DescriptionInfo from './descriptionInfo.vue'
import ApplicationConfig from './applicationConfig.vue'
import OpenQuestion from './openQuestion.vue'
import PostChannel from './postChannel.vue'
import PersonalityCalibration from './personalityCalibration.vue'
import AIJobFitCriterion from './aiJobFitCriterion.vue'
import JobFit from './jobFit/index.vue'
import StatusHistory from './statusHistory.vue'
import AIInterviewRoom from './aiInterviewRoom.vue'
import { JOB_EDITOR_STEP_TYPE, VISIBILITY_TYPE } from '@/constant/job'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'

const STEP_TYPE = JOB_EDITOR_STEP_TYPE

export default {
  components: {
    StepFormLayout,
    DialogWrapper,
    BaseInfo,
    AdditionalInfo,
    DescriptionInfo,
    ApplicationConfig,
    OpenQuestion,
    PostChannel,
    PersonalityCalibration,
    StatusHistory,
    AIJobFitCriterion,
    AIInterviewRoom,
    JobFit
  },
  props: {
    isTemplate: {
      type: Boolean,
      default: false
    },
    isEditMode: {
      type: Boolean,
      default: false
    },
    value: {
      type: Object,
      default: null
    },
    isPreview: {
      type: Boolean,
      default: false
    },
    hiddenSaveBtn: {
      type: Boolean,
      default: false
    }
  },
  data () {
    let mountPromiseResolve = null
    const mountPromise = new Promise((resolve, reject) => {
      mountPromiseResolve = resolve
    })
    const i18nPrefix = 'admin.job.editor'
    const vm = this
    return {
      STEP_TYPE,
      mountPromise,
      mountPromiseResolve,
      i18nPrefix,
      jobInfo: {},
      companyListLoader: null,
      companyList: [],
      company: null,
      rootCompany: null,
      selfCompany: null,
      hasUnsavedStep: false,
      submittedStep: [],
      showStep: false,
      showStandaloneStep: false,
      standaloneStepConfig: null,
      showAiMatchTestCandidate: false,
      currentStepIndex: 0,
      currentStandaloneStepIndex: -1,
      stepConfigList: [
        {
          stepType: STEP_TYPE.BASE_INFO,
          labelI18nKey: `${i18nPrefix}.label.stepName.${STEP_TYPE.BASE_INFO}`,
          iconClass: 'iconfont icona-BasicJobInformation',
          component: BaseInfo,
          hiddenTail: false,
          props: {
            get disabled () {
              return vm.hiddenSaveBtn
            },
            get company () {
              return vm.company
            }
          }
        },
        {
          stepType: STEP_TYPE.ADDITIONAL_INFO,
          labelI18nKey: `${i18nPrefix}.label.stepName.${STEP_TYPE.ADDITIONAL_INFO}`,
          iconClass: 'iconfont icona-AdditionalInformation',
          component: AdditionalInfo,
          hiddenTail: false,
          props: {
            get disabled () {
              return vm.hiddenSaveBtn
            },
            get company () {
              return _.find(vm.companyList, { id: vm.jobInfo.company?.id })
            }
          }
        },
        {
          stepType: STEP_TYPE.DESCRIPTION_INFO,
          labelI18nKey: `${i18nPrefix}.label.stepName.${STEP_TYPE.DESCRIPTION_INFO}`,
          iconClass: 'iconfont icona-JobDescription',
          component: DescriptionInfo,
          hiddenTail: false,
          props: {
            get disabled () {
              return vm.hiddenSaveBtn
            },
            get rootCompany () {
              return vm.rootCompany
            }
          }
        },
        {
          stepType: STEP_TYPE.POST_CHANNEL,
          labelI18nKey: `${i18nPrefix}.label.stepName.${STEP_TYPE.POST_CHANNEL}`,
          iconClass: 'iconfont icona-JobPostingDistribution',
          component: PostChannel,
          hiddenTail: false,
          props: {
            get disabled () {
              return vm.hiddenSaveBtn
            },
            get rootCompany () {
              return vm.rootCompany
            }
          }
        },
        {
          stepType: STEP_TYPE.APPLICATION_CONFIG,
          labelI18nKey: `${i18nPrefix}.label.stepName.${STEP_TYPE.APPLICATION_CONFIG}`,
          iconClass: 'iconfont icona-ApplicationConfiguration',
          component: ApplicationConfig,
          hiddenTail: false,
          props: {
            get disabled () {
              return vm.hiddenSaveBtn
            }
          }
        },
        {
          stepType: STEP_TYPE.OPEN_QUESTION,
          labelI18nKey: `${i18nPrefix}.label.stepName.${STEP_TYPE.OPEN_QUESTION}`,
          iconClass: 'iconfont icona-ApplicantQuestionnaireScreening',
          component: OpenQuestion,
          hiddenTail: false,
          props: {
            get disabled () {
              return vm.hiddenSaveBtn
            }
          }
        },
        {
          stepType: STEP_TYPE.PERSONALITY_CALIBRATION,
          labelI18nKey: `${i18nPrefix}.label.stepName.${STEP_TYPE.PERSONALITY_CALIBRATION}`,
          iconClass: 'iconfont icona-PersonalityFitBenchmark',
          component: PersonalityCalibration,
          hiddenTail: false,
          props: {
            get disabled () {
              return vm.hiddenSaveBtn
            }
          }
        },
        {
          stepType: STEP_TYPE.AI_INTERVIEW_ROOM,
          labelI18nKey: `${i18nPrefix}.label.stepName.${STEP_TYPE.AI_INTERVIEW_ROOM}`,
          iconClass: 'iconfont icona-CandidateAIComparisonTable',
          component: AIInterviewRoom,
          hiddenTail: false,
          props: {
            get disabled () {
              return vm.hiddenSaveBtn
            }
          }
        },
        {
          stepType: STEP_TYPE.STATUS_HISTORY,
          labelI18nKey: `${i18nPrefix}.label.stepName.${STEP_TYPE.STATUS_HISTORY}`,
          iconClass: 'iconfont icona-JobStatusHistory',
          component: StatusHistory,
          hiddenTail: false,
          props: {
            get disabled () {
              return vm.hiddenSaveBtn
            }
          }
        }
      ],
      standaloneStepMenuList: [
        {
          stepType: STEP_TYPE.AI_COMPARISON_CRITERION,
          labelI18nKey: `${i18nPrefix}.label.stepName.${STEP_TYPE.AI_COMPARISON_CRITERION}`,
          iconClass: 'iconfont icona-CandidateAIComparisonTable',
          component: AIJobFitCriterion,
          hiddenTail: false,
          props: {
            get disabled () {
              return vm.hiddenSaveBtn
            }
          }
        },
        {
          stepType: STEP_TYPE.JOB_FIT_CONDITION,
          labelI18nKey: `${i18nPrefix}.label.stepName.${STEP_TYPE.JOB_FIT_CONDITION}`,
          iconClass: 'iconfont icona-CandidateAIComparisonTable',
          component: JobFit,
          hiddenTail: false
        }
      ],
      defaultQuestionListLoader: null,
      defaultQuestionList: [],
      showSaveJobTipDialog: {
        show: false,
        stepType: null
      }
    }
  },
  computed: {
    jobCompanyId () {
      return this.jobInfo?.company?.id
    },
    isSelfCompanyJob () {
      if (this.isEditMode) {
        return _.findIndex(this.companyList, { id: this.selfCompany?.id }) > -1
      } else {
        return true
      }
    },
    hasAssessmentPermission () {
      return this.hasAnyPermission('PREMIUM_ASSESSMENTS')
    },
    isShowPersonalityCalibrationStep () {
      return this.hasAssessmentPermission
    },
    hasEditAnyJobPermission () {
      return this.hasAnyPermission('PERM_EDIT_ANY_JOB')
    },
    hasAIComparisonPermission () {
      return this.hasAnyPermission('PREMIUM_AI_COMPARISON')
    },
    hasAIInterviewPermission () {
      return this.hasAnyPermission('PREMIUM_AI_INTERVIEW')
    },
    canViewAIFitScore () {
      return this.hasAnyPermission(['PREMIUM_AI_RESUME_MATCH', 'PERM_HR_VIEW_AI_FIT_SCORE'])
    },
    companyIsELC () {
      return this.hasAnyPermission('COMPUTED_COMPANY_IS_ELC') || (this.hasEditAnyJobPermission && this.rootCompany?.id === 6172)
    },
    activeStepConfigList () {
      const hiddenStep = []
      if (this.isTemplate) {
        hiddenStep.push(STEP_TYPE.ADDITIONAL_INFO, STEP_TYPE.POST_CHANNEL, STEP_TYPE.STATUS_HISTORY, STEP_TYPE.AI_INTERVIEW_ROOM)
      } else {
        if (!this.isEditMode) {
          hiddenStep.push(STEP_TYPE.STATUS_HISTORY)
          if (this.jobInfo.personality_required !== '1') {
            hiddenStep.push(STEP_TYPE.PERSONALITY_CALIBRATION)
          }
        }
      }
      if (!this.isShowPersonalityCalibrationStep) {
        hiddenStep.push(STEP_TYPE.PERSONALITY_CALIBRATION)
      }
      if (!this.hasAIInterviewPermission) {
        hiddenStep.push(STEP_TYPE.AI_INTERVIEW_ROOM)
      }

      return this.stepConfigList.filter(item => !(item.disable || hiddenStep.includes(item.stepType)))
    },
    activeStepList () {
      return this.activeStepConfigList.map(item => item.stepType)
    },
    activeStandaloneStepConfigList () {
      const hiddenStep = []
      if (!this.hasAIComparisonPermission) {
        hiddenStep.push(STEP_TYPE.AI_COMPARISON_CRITERION)
      }
      if (!this.canViewAIFitScore) {
        hiddenStep.push(STEP_TYPE.JOB_FIT_CONDITION)
      }
      return this.standaloneStepMenuList.filter(item => !(item.disable || hiddenStep.includes(item.stepType)))
    },
    stepType () {
      return this.$route.query.step
    },
    stepConfig () {
      return this.activeStepConfigList.find(item => item.stepType === this.stepType)
    },
    isQuestionStepActive () {
      return !this.isTemplate && this.showStep && this.stepConfig?.stepType === STEP_TYPE.OPEN_QUESTION
    },
    stepTitle () {
      return getI18nPropertyValue(this.jobInfo, 'cn_title', 'title')
    },
    companyDataParams () {
      return { company_id: this.isSelfCompanyJob ? null : this.jobCompanyId }
    },
    hiddenStepBtn () {
      return this.showStandaloneStep && !!this.standaloneStepConfig
    },
    isJobFitStepBtn () {
      return this.standaloneStepConfig?.stepType === STEP_TYPE.JOB_FIT_CONDITION
    }
  },
  created () {

  },
  mounted () {
    window.onbeforeunload = this.handleWindowBeforeUnload
    this.mountPromiseResolve()
  },
  methods: {
    ...mapActions('company', ['getCompanyChildrenTree']),
    ...mapActions('openQuestion', ['getDefaultQuestionTemplate']),
    flatCompanyChildrenTreeMap (resultList, company, treeMap) {
      addI18nProperty(company, 'i18nName', 'cn_name', 'name')
      resultList.push(company)
      _.forEach(treeMap[company.id], child => {
        this.flatCompanyChildrenTreeMap(resultList, child, treeMap)
      })
    },
    async loadCompanyList () {
      if (!this.companyListLoader) {
        this.companyListLoader = this.getCompanyChildrenTree({ company_id: this.jobInfo?.company?.id, permissions: 'receive_resume_email' }).then(resp => {
          const currentCompany = resp.self
          const childrenMap = resp.children
          const rootCompany = resp.root
          const selfCompany = resp.hr_self
          const companyList = []
          this.flatCompanyChildrenTreeMap(companyList, currentCompany, childrenMap)
          this.companyList = companyList
          this.company = currentCompany
          this.rootCompany = rootCompany
          this.selfCompany = selfCompany
        })
      }
      await this.companyListLoader
      return this.companyList
    },
    async loadDefaultQuestionList () {
      if (!this.defaultQuestionListLoader) {
        this.defaultQuestionListLoader = this.getDefaultQuestionTemplate(this.companyDataParams).then(resp => {
          if (resp) {
            this.defaultQuestionList = resp
          }
          return this.defaultQuestionList
        })
      }
      await this.defaultQuestionListLoader
      return this.defaultQuestionList
    },
    async submitCurrentStep (ignoreError) {
      if (this.isCurrentStepHasChangedData()) {
        this.hasUnsavedStep = true
      }
      const stepConfig = this.stepConfig
      const stepComponent = this.$refs[`stepComponent_${stepConfig.stepType}`][0]
      const success = await stepComponent.submit(ignoreError)
      const submittedStep = this.submittedStep
      if (success && !submittedStep.includes(stepConfig)) {
        submittedStep.push(stepConfig)
      } else if (!success) {
        _.remove(submittedStep, stepConfig)
      }
      return success
    },
    isCurrentStepHasChangedData () {
      const currentStepType = this.showStep ? this.stepConfig?.stepType : this.standaloneStepConfig?.stepType
      const stepComponents = this.$refs[`stepComponent_${currentStepType}`]
      if (!stepComponents) {
        return false
      }
      return stepComponents[0].hasChangedData
    },
    getOpenQuestionIdentity (question) {
      if (!question) return ''
      if (question.id !== null && question.id !== undefined && question.id !== '') return `id:${question.id}`
      if (question._vid !== null && question._vid !== undefined && question._vid !== '') return `vid:${question._vid}`
      return `text:${question.title || ''}`
    },
    async resetDefaultQuestion () {
      if (this.isTemplate) {
        return
      }
      await this.loadCompanyList()
      await this.loadDefaultQuestionList()
      if (this.rootCompany?.id !== 3738) {
        return
      }
      const defaultQuestionList = this.defaultQuestionList
      const questionList = this.jobInfo.questions || []
      const isRemove = this.jobInfo.job_visibility === VISIBILITY_TYPE.E
      if (isRemove) {
        const defaultQuestionIdentities = _.map(defaultQuestionList, this.getOpenQuestionIdentity)
        _.remove(questionList, question => defaultQuestionIdentities.includes(this.getOpenQuestionIdentity(question)))
      } else {
        _.forEach(defaultQuestionList, question => {
          const identity = this.getOpenQuestionIdentity(question)
          const found = _.find(questionList, item => this.getOpenQuestionIdentity(item) === identity)
          if (found) {
            found.visibility = this.jobInfo.job_visibility
            return
          }
          questionList.push({
            ...question,
            visibility: this.jobInfo.job_visibility || VISIBILITY_TYPE.IE,
            options: question.options && _.map(question.options, (item, index) => {
              return {
                ...item,
                order: index + 1
              }
            }),
            order: questionList.length
          })
        })
      }
      this.jobInfo.questions = questionList
    },
    async showStepPage (stepConfig, isFromStandaloneMenu = false) {
      if (!stepConfig || !this.activeStepList.includes(stepConfig.stepType)) {
        stepConfig = this.activeStepConfigList[0]
      }
      const stepType = stepConfig.stepType
      if (stepType === this.stepType) {
        if (!isFromStandaloneMenu && this.isCurrentStepHasChangedData()) {
          const confirm = window.confirm(this.$t(`${this.i18nPrefix}.message.refreshPageWithoutSaveData`))
          if (!confirm) {
            return
          }
        }
        this.showStep = false
        await this.$nextTick()
        this.showStep = true
      } else {
        const query = { ...this.$route.query, step: stepType, standalone_step: undefined }
        if (this.stepType) {
          await this.$router.push({ query })
        } else {
          await this.$router.replace({ query })
        }
      }
      this.currentStepIndex = _.findIndex(this.activeStepConfigList, { stepType })
    },
    async handleStepChange (targetIndex) {
      const isFromStandaloneMenu = this.currentStandaloneStepIndex >= 0
      if (isFromStandaloneMenu && this.isCurrentStepHasChangedData()) {
        const confirm = window.confirm(this.$t(`${this.i18nPrefix}.message.refreshPageWithoutSaveData`))
        if (!confirm) {
          return
        }
        window.onbeforeunload = null
      }
      this.showStandaloneStep = false
      this.standaloneStepConfig = null
      this.currentStandaloneStepIndex = -1
      this.showStep = true
      await this.$nextTick()
      let targetStep = this.activeStepConfigList[targetIndex]
      const currentIndex = this.currentStepIndex
      let needCheck = false
      if (targetIndex > currentIndex) {
        const success = await this.submitCurrentStep()
        if (!success) {
          return
        }
        const nextIndex = currentIndex + 1
        if (targetIndex > nextIndex) {
          const pastIndexList = _.range(nextIndex, targetIndex)
          _.forEach(pastIndexList, index => {
            const stepConfig = this.activeStepConfigList[index]
            if (!this.submittedStep.includes(stepConfig)) {
              targetStep = stepConfig
              needCheck = true
              return false
            }
          })
        }
      } else if (targetIndex < currentIndex) {
        await this.submitCurrentStep(true)
      }
      await this.showStepPage(targetStep, isFromStandaloneMenu)
      if (needCheck) {
        // wait step change
        await this.$nextTick()
        // wait step data render
        await this.$nextTick()
        this.submitCurrentStep()
      }
    },
    async handleActiveStandaloneMenuChange (targetIndex) {
      if (!this.isEditMode) {
        this.showSaveJobTipDialog.stepType = this.activeStandaloneStepConfigList[targetIndex].stepType
        if (this.currentStepIndex < this.activeStepConfigList.length - 1) {
          await this.handleStepChange(this.activeStepConfigList.length - 1)
          return
        }
        this.handleSaveAndBackBtnClick(true)
        return
      }
      let hasUnsavedData = false
      if (this.currentStandaloneStepIndex < 0 && this.hasUnsavedData()) {
        hasUnsavedData = true
      } else if (this.currentStandaloneStepIndex >= 0 && this.isCurrentStepHasChangedData()) {
        hasUnsavedData = true
      }
      if (hasUnsavedData) {
        const confirm = window.confirm(this.$t(`${this.i18nPrefix}.message.refreshPageWithoutSaveData`))
        if (!confirm) {
          return
        }
        window.onbeforeunload = null
      }
      this.showStep = false
      this.showStandaloneStep = true
      this.standaloneStepConfig = this.activeStandaloneStepConfigList[targetIndex]
      this.currentStandaloneStepIndex = targetIndex
      this.$router.replace({ query: { ...this.$route.query, standalone_step: this.standaloneStepConfig.stepType } })
    },
    hasUnsavedData () {
      return this.hasUnsavedStep || this.isCurrentStepHasChangedData()
    },
    handleWindowBeforeUnload ($event) {
      if (this.hasUnsavedData()) {
        return ''
      } else {
        window.onbeforeunload = null
      }
    },
    handlePrevBtnClick () {
      const lastConfigIndex = this.activeStepConfigList.indexOf(this.stepConfig) - 1
      this.handleStepChange(lastConfigIndex)
    },
    handleCancelBtnClick () {
      if (this.hasUnsavedData()) {
        const confirm = window.confirm(this.$t(`${this.i18nPrefix}.message.refreshPageWithoutSaveData`))
        if (!confirm) {
          return
        }
      }
      window.onbeforeunload = null
      this.$emit('cancel')
    },
    async handlePreviewBtnClick () {
      const success = await this.submitCurrentStep()
      if (!success) {
        return
      }
      this.$emit('preview', { ...this.jobInfo, hasUnsavedData: this.hasUnsavedData() })
    },

    async handleSaveBtnClick () {
      const success = await this.submitCurrentStep()
      if (!success) {
        return
      }
      const workTime = this.jobInfo.work_time?.id
      // 注意：普通保存也需要处理子题关联，由父组件在保存成功后调用
      this.$emit('save', { ...this.jobInfo, work_time: { id: workTime === -1 ? null : workTime } })
      this.hasUnsavedStep = false
    },
    async handleSaveAndBackBtnClick (isDialogConfirm) {
      this.showSaveJobTipDialog.show = false
      const success = await this.submitCurrentStep()
      if (!success) {
        return
      }
      const workTime = this.jobInfo.work_time?.id
      let standaloneStep
      let step
      if (isDialogConfirm) {
        step = STEP_TYPE.STATUS_HISTORY
        standaloneStep = this.showSaveJobTipDialog.stepType
        this.showSaveJobTipDialog.stepType = null
      }
      // 注意：不在这里调用 processOpenQuestionLinks
      // 子题关联处理需要在父组件保存职位获取真实 ID 后再调用
      // 父组件保存成功后会通过 @open-question-links-processed 事件或回调处理
      this.$emit('save-back', { ...this.jobInfo, work_time: { id: workTime === -1 ? null : workTime } }, step, standaloneStep)
      this.hasUnsavedStep = false
    },
    // 处理 open question 的子题关联（供父组件在保存成功后调用）
    async processOpenQuestionLinks (savedJobInfo) {
      // 必须有有效的职位 ID
      if (!savedJobInfo || !savedJobInfo.id) {
        // eslint-disable-next-line no-console
        console.warn('processOpenQuestionLinks called without valid job id')
        return
      }
      // 获取 openQuestion 步骤组件的引用
      const openQuestionSteps = this.$refs.stepComponent_openQuestion
      if (openQuestionSteps && openQuestionSteps[0]) {
        const openQuestionRef = openQuestionSteps[0]
        if (openQuestionRef.afterSaveProcessLinks) {
          try {
            await openQuestionRef.afterSaveProcessLinks(savedJobInfo)
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to process open question links:', error)
          }
        }
      }
    },
    async handleAiComparisonSaveBtnClick () {
      if (!this.standaloneStepConfig.stepType || !this.hasAIComparisonPermission) {
        return
      }
      // AI 对比
      await this.$nextTick()
      const comparisonStepComponent = this.$refs[`stepComponent_${STEP_TYPE.AI_COMPARISON_CRITERION}`][0]
      const success = await comparisonStepComponent.submit()
      if (!success) {
        return
      }
      const comparisonCriterionList = await comparisonStepComponent.getAllCriterionList()
      const autoBack = this.isEditMode
      this.$emit('ai-job-comparison-save-back', comparisonCriterionList, autoBack)
    },
    async handleJobFitSaveBtnClick () {
      if (!this.standaloneStepConfig.stepType || !this.canViewAIFitScore) {
        return
      }
      await this.$nextTick()
      const component = this.$refs[`stepComponent_${STEP_TYPE.JOB_FIT_CONDITION}`][0]
      const success = await component.submit()
      if (!success) {
        return
      }
      const jobFitCondition = await component.getConditionData()
      const autoBack = this.isEditMode
      this.$emit('job-fit-save-back', jobFitCondition, autoBack)
    },
    handleNextBtnClick () {
      const nextConfigIndex = this.activeStepConfigList.indexOf(this.stepConfig) + 1
      this.handleStepChange(nextConfigIndex)
    }
  },
  watch: {
    'jobInfo.job_visibility' () {
      this.resetDefaultQuestion()
    },
    value: {
      immediate: true,
      async handler () {
        let jobInfo = {}
        if (this.value) {
          const workTime = this.value.work_time?.id
          jobInfo = { ...this.value }
          if (this.isEditMode) {
            jobInfo.work_time = { id: workTime || -1 }
          }
        }
        this.jobInfo = jobInfo
        this.$eventBus.$emit('showLoading', true)
        try {
          await this.mountPromise
          await this.loadCompanyList()
          await this.resetDefaultQuestion()
          const stepConfig = this.isEditMode ? this.stepConfig : this.activeStepConfigList[0]
          if (this.isEditMode) {
            this.submittedStep = _.map(this.activeStepConfigList, item => item)
          } else {
            this.submittedStep = []
          }
          await this.showStepPage(stepConfig)
          this.showStep = true
          if (this.$route.query.standalone_step) {
            this.standaloneStepConfig = this.activeStandaloneStepConfigList.find(item => item.stepType === this.$route.query.standalone_step)
            if (this.standaloneStepConfig) {
              this.showStep = false
              await this.$nextTick()
              this.showStandaloneStep = true
              this.currentStandaloneStepIndex = this.activeStandaloneStepConfigList.findIndex(item => item.stepType === this.standaloneStepConfig.stepType)
            }
            // const { standaloneStep, ...query } = this.$route.query
            // this.$router.replace({ query })
          }
        } finally {
          this.$eventBus.$emit('showLoading', false)
        }
      }
    },
    activeStepConfigList () {
      let lastStepConfig = null
      _.forEach(this.activeStepConfigList, stepConfig => {
        if (lastStepConfig) {
          lastStepConfig.hiddenTail = stepConfig.stepType === STEP_TYPE.STATUS_HISTORY
        }
        stepConfig.hiddenTail = false
        lastStepConfig = stepConfig
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.job-editor-layout {
  display: flex;
  margin: 0 auto;
  border-radius: 10px;
  background-color: #f7f7f7;
  box-sizing: border-box;
  position: relative;

  .preview-page {
    position: absolute;
    top: 0;
    left: 0;
    background-color: #cccccc;
    z-index: 99999999;
  }

  .layout-aside {
    width: 300px;
    flex-shrink: 0;
    padding: 60px 15px 50px;
    border-radius: 10px;
    background-color: #6c6c6c;

    .step-list {
      position: relative;

      .active-step-cover {
        width: 100%;
        height: 50px;
        border-radius: 5px;
        background-color: #f7f7f7;
        overflow: hidden;
        transition: top .3s;
        position: absolute;
        top: 0;
        left: 0;
        z-index: 1;

        .cover-step-list {
          transform: translateY(0);
          transition: transform .3s;

          .step {
            .step-name, .step-icon {
              color: inherit;
            }
          }
        }
      }

      .step {
        display: flex;
        height: 50px;
        margin-bottom: 15px;
        padding: 15px 25px;
        border-radius: 5px;
        align-items: center;
        cursor: pointer;
        position: relative;

        &.is-active {
          .step-node {
            background-color: #9c9c9c;

            .node-icon {
              font-size: 16px;
              color: #ffffff;

              &::before {
                content: "\e794";
                margin-top: 2px;
                margin-left: 2px;
              }
            }
          }

          ~ .step {
            .step-node {
              padding: 4px;
              background-color: #9c9c9c;

              .node-icon {
                display: block;
                width: 100%;
                height: 100%;
                border-radius: 50%;
                background-color: #ffffff;

                &::before {
                  content: none;
                }
              }
            }
          }
        }

        &:last-child {
          .step-tail {
            display: none;
          }
        }

        .step-tail {
          height: 100%;
          padding-bottom: 15px;
          box-sizing: content-box;
          border-left: 2px solid #9c9c9c;
          position: absolute;
          top: 15px;
          left: 64px;
        }

        .step-node {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          line-height: 1;
          background-color: #ffffff;
          transform: translate(-50%, -50%);
          color: #14a965;
          position: absolute;
          top: 50%;
          left: 65px;

          .node-icon {
            font-family: element-icons!important;
            speak: none;
            font-style: normal;
            font-weight: 400;
            font-variant: normal;
            text-transform: none;
            line-height: 1;
            vertical-align: baseline;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            font-size: 24px;

            &::before {
              content: "\e79c";
              display: block;
              margin-top: -2px;
              margin-left: -2px;
            }
          }
        }

        .step-icon {
          width: 22px;
          color: #ffffff;

          i {
            font-size: 22px;
          }
        }

        .step-name {
          margin-left: 40px;
          vertical-align: middle;
          color: #ffffff;
        }
      }
    }
  }

  &.new-page-layout {
    ::v-deep {
      .layout-aside {
        border-radius: unset;
        padding: 27px 12px 50px 6px;
        background-color: #FFFFFF;

        .step-menu-item {

          .step-tail {
            z-index: 1;
          }

          .step-node {
            color: #606266;
            z-index: 1;

            &.available {
              color: #14a965;
            }
          }

          .step-icon {
            color: #606266;
          }
          .step-name {
            color: #606266;
          }
        }

        .step-menu-item.is-active {
          background-color: #F2FCFF;
          color: #0096cc;

          .step-node {
            color: #0096cc;
          }

          .step-icon {
            color: #0096cc;
          }
          .step-name {
            color: #0096cc;
          }

          .processing {
            background-color: #0096cc;
          }
        }
      }

      .layout-main {
        max-width: unset;
        margin: 0 20px;
        align-items: center;
        background-color: #FFFFFF;

        .layout-content {
          width: 980px;
        }
      }
    }
  }

  &.show-standalone-step {
    ::v-deep {
      .layout-aside {
        .step-menu {
          .active-menu-item-cover {
            background-color: unset;
          }
        }
      }

      .layout-main {
        .layout-content {
          .form-action-btn-group {
            .cancel-btn {
              margin-right: 0;
            }

            .reset-btn {
              margin-right: auto;
            }
          }
        }
      }
    }
  }

  .layout-body {
    flex: 1;
    display: flex;
    width: 63vw;
    min-width: 700px;
    max-width: 1035px;
    padding: 60px 40px 50px;
    flex-direction: column;

    .body-title {
      padding-bottom: 15px;
      border-bottom: 1px solid #cccccc;
      font-size: 18px;
    }

    .body-content {
      flex: 1;
      margin: 50px;
    }

    .body-footer {
      &.footer-btn-group {
        display: flex;
        justify-content: space-between;

        .footer-btn {
          padding: 5px 25px;
          line-height: normal;

          &.cancel-btn {
            margin-right: auto;
          }
        }
      }
    }
  }
}
</style>
